import { trpc } from './trpc';

// Analytics query filters type
export type AnalyticsFilters = {
    startDate: Date;
    endDate: Date;
    storeIds: string[];
    currency: string;
};

// V4 (aggregated maps) - Legacy, to be replaced by individual hooks
export function useAnalyticsV4(params: {
    filters: AnalyticsFilters;
    metrics: (
        | 'rebillRevenue'
        | 'refunds'
        | 'straightSaleRevenue'
        | 'upsellRevenue'
        | 'activeSubscriptions'
        | 'subscriptionsGained'
        | 'subscriptionsCancelled'
        | 'approvalRatesByBillingCycle'
        | 'averageLTV'
    )[];
    enabled?: boolean;
}) {
    return trpc.analytics.v4.useQuery(params, {
        enabled: params.enabled !== false,
    });
}

// Individual metric hooks (analytics-fast pattern)
export function useRebillRevenueData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.rebillRevenue.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useRefundsData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.refunds.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useStraightSaleData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.straightSale.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useUpsellRevenueData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.upsellRevenue.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useActiveSubscriptionsData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.activeSubscriptions.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useSubscriptionsGainedData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.subscriptionsGained.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useSubscriptionsCancelledData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.subscriptionsCancelled.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useApprovalRatesData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.approvalRates.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useAverageLTVData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.averageLTV.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useSubscriptionsByStatusData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.subscriptionsByStatus.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useSubscriptionsByStatusTimeSeriesData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.subscriptionsByStatusTimeSeries.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

// Subscription Calendar hooks
export function useSubscriptionCalendar(
    params: {
        storeIds: string[];
        currency: string;
        monthDate: Date;
    },
    options?: { enabled?: boolean }
) {
    return trpc.analytics.subscriptionCalendar.useQuery(params, {
        enabled: options?.enabled !== false,
        staleTime: 5 * 60 * 1000, // 5 minutes - calendar data doesn't change frequently
    });
}

export function useSubscriptionCalendarDetails(
    params: {
        date: string;
        storeIds: string[];
        currency: string;
    },
    options?: { enabled?: boolean }
) {
    return trpc.analytics.subscriptionCalendarDetails.useQuery(params, {
        enabled: options?.enabled !== false && !!params.date,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

// Upsell Take Rate hooks
export function useUpsellTakeRateData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.upsellTakeRate.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

// Conversion Funnel hooks
export function useCustomerConversionFunnelData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.customerConversionFunnel.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useCheckoutToOrderPaidTimeData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.checkoutToOrderPaidTime.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useCheckoutToEmailEnteredTimeData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.checkoutToEmailEnteredTime.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useCheckoutToBillingAddressTimeData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.checkoutToBillingAddressTime.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useConversionOverTimeData(
    filters: AnalyticsFilters & { granularity?: 'daily' | 'weekly' | 'monthly' },
    options?: { enabled?: boolean }
) {
    return trpc.analytics.conversionOverTime.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useRevenueByProcessorData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.revenueByProcessor.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useTransactionErrorsData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.transactionErrors.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useRefundsByTypeData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.refundsByType.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useRevenueByProcessorTimeSeriesData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.revenueByProcessorTimeSeries.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useTransactionErrorsTimeSeriesData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.transactionErrorsTimeSeries.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useCustomerGeographicByCardCountryData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.customerGeographicByCardCountry.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useCustomerGeographicByIpCountryData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.customerGeographicByIpCountry.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

// Product Analytics hooks
export function useProductsByRevenueData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.productsByRevenue.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useVariantsByRevenueData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.variantsByRevenue.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useTopSellingProductsData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.topSellingProducts.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

export function useProductSalesOverTimeData(filters: AnalyticsFilters, options?: { enabled?: boolean }) {
    return trpc.analytics.productSalesOverTime.useQuery(filters, {
        enabled: options?.enabled !== false,
    });
}

// V2 (single queryKey)
export function useAnalyticsV2<TParams extends Record<string, unknown>>(
    input: {
        queryKey: any;
        params?: TParams;
    },
    options?: { enabled?: boolean }
) {
    return trpc.analytics.v2.useQuery(input as any, {
        enabled: options?.enabled !== false,
    });
}

// V3 (single queryKey)
export function useAnalyticsV3<TParams extends Record<string, unknown>>(
    input: {
        queryKey:
        | 'straightSaleRevenue'
        | 'rebillRevenue'
        | 'upsellRevenue'
        | 'refunds'
        | 'totalSubscribers'
        | 'subscribersGained'
        | 'subscribersLost'
        | 'approvalRateByCycle'
        | 'approvalRateByProcessor'
        | 'upsellTakeRateByPosition'
        | 'upsellTakeRateOverall'
        | 'averageOrderValue';
        params?: TParams;
    },
    options?: { enabled?: boolean }
) {
    return trpc.analytics.v3.useQuery(input as any, {
        enabled: options?.enabled !== false,
    });
}


